import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { client } from "@/services/client"
import type { Paths } from "@/services/types"
import { useQueryKey } from "@/services/utils"
import { fetchOrThrow } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { ScheduleMisalignmentDetail } from "../pages/schedule-misalignment-route/schedule-misalignment.types"
import { useWindowQuery } from "./use-window-query"
import type { WindowQuery } from "./use-window-query"

// insights-api models `organization_id` (required int) on the /api/v1/neat
// operations. `window`/`start`/`end` are honored by insights-api + forwarded by
// the BFF but not yet in the regenerated schema — extend locally.
type ScheduleMisalignmentQuery = NonNullable<
  Paths["/api/v1/neat/insights/{slug}"]["get"]["parameters"]["query"]
> &
  WindowQuery

const getScheduleMisalignment = async (
  organizationId: number,
  win: WindowQuery
): Promise<ScheduleMisalignmentDetail> => {
  const query: ScheduleMisalignmentQuery = {
    organization_id: organizationId,
    ...win,
  }

  // BFF path is templated `/api/v1/neat/insights/{slug}` — pass slug via
  // path params, same as `useGetVariance`. Field names align with the
  // generated response; typed pass-through.
  return (await fetchOrThrow(() =>
    client().GET("/api/v1/neat/insights/{slug}", {
      params: { path: { slug: "schedule-misalignment" }, query },
    })
  )) as ScheduleMisalignmentDetail
}

export const useGetScheduleMisalignment = () => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const queryKey = useQueryKey(
    ApiQueryName.ScheduleMisalignment,
    "getOne",
    null,
    win
  )
  return useQuery<ScheduleMisalignmentDetail>({
    queryKey,
    enabled: activeOrganizationId != null,
    queryFn: () => getScheduleMisalignment(Number(activeOrganizationId), win),
  })
}
